import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Typography from '@tiptap/extension-typography';
import CharacterCount from '@tiptap/extension-character-count';
import Youtube from '@tiptap/extension-youtube';
import { common, createLowlight } from 'lowlight';
import { EditorToolbar } from '../../blog/components/PostEditor/EditorToolbar';
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload';

const lowlight = createLowlight(common);

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

/**
 * Controlled TipTap editor for content-pages, built on the exact same extension
 * list and prose classes as the blog's `BlockEditor` (PE-2175 reuse requirement) —
 * but that component is uncontrolled and takes no props, so it can't be shared
 * as-is. This wraps `{content, onChange}` around the same TipTap setup instead of
 * duplicating BlockEditor with props bolted on.
 *
 * The image button goes through `useMediaUpload` rather than EditorToolbar's
 * default `window.prompt`, via the optional `onRequestImage` hook added there.
 */
export const RichTextEditor = ({ content, onChange, placeholder }: RichTextEditorProps) => {
  const { upload } = useMediaUpload();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Image,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Start writing...',
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Typography,
      CharacterCount,
      Youtube,
    ],
    content,
    editorProps: {
      attributes: {
        class: 'w-full min-h-[500px] bg-transparent border-none outline-none resize-none prose prose-lg dark:prose-invert max-w-none focus:outline-none p-4',
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep the editor in sync with an externally-driven `content` change (e.g. a
  // fetched page loading after the editor already mounted) — but never clobber
  // what the user is actively typing, so this only fires when the incoming value
  // actually differs from what the editor already holds.
  useEffect(() => {
    if (!editor) return;
    if (content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const handleRequestImage = async (): Promise<string | null> => {
    const file = await new Promise<File | null>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = () => resolve(input.files?.[0] ?? null);
      input.click();
    });
    if (!file) return null;
    const uploaded = await upload(file);
    return uploaded.url;
  };

  return (
    <div className="space-y-4">
      <EditorToolbar editor={editor} onRequestImage={handleRequestImage} />
      <div className="border rounded-md bg-background shadow-sm min-h-[500px]">
        <EditorContent editor={editor} className="text-lg text-foreground" />
      </div>
    </div>
  );
};
